import { collectMarkdownCodeRanges, parseMarkdownAst } from "./markdown-parser";

export type NormalizedMarkdownSource = {
  source: string;
  outputToInputOffset: number[];
};

type ListLine = {
  containerKey: string;
  indentStart: number;
  indentLength: number;
  markerOffset: number;
  markerWidth: number;
};

/**
 * Treat a relative increase of 1–4 spaces as one list level, regardless of
 * marker type. The tokenizer still receives conventional four-column nesting,
 * while offsets remain traceable to the exact author source.
 */
export function normalizeFlexibleListIndentation(source: string): NormalizedMarkdownSource {
  const protectedRanges = collectMarkdownCodeRanges(parseMarkdownAst(source));
  const outputToInputOffset: number[] = [];
  let output = "";
  let lineStart = 0;
  let activeContainer = "";
  let rawIndentStack: number[] = [];
  let normalizedStepStack: number[] = [];

  const appendOriginal = (start: number, end: number) => {
    for (let offset = start; offset < end; offset += 1) {
      outputToInputOffset.push(offset);
      output += source[offset];
    }
  };
  const appendNormalizedSpaces = (count: number, inputStart: number, inputLength: number) => {
    for (let index = 0; index < count; index += 1) {
      outputToInputOffset.push(inputStart + Math.min(index, Math.max(0, inputLength - 1)));
      output += " ";
    }
  };

  while (lineStart < source.length) {
    const breakOffset = source.indexOf("\n", lineStart);
    const lineEnd = breakOffset === -1 ? source.length : breakOffset;
    const line = source.slice(lineStart, lineEnd);
    const parsed = parseListLine(line, lineStart);
    const markerProtected = parsed && protectedRanges.some((range) => range.start <= parsed.markerOffset && parsed.markerOffset < range.end);

    if (parsed && !markerProtected) {
      if (parsed.containerKey !== activeContainer) {
        activeContainer = parsed.containerKey;
        rawIndentStack = [];
        normalizedStepStack = [];
      }
      const rawIndent = parsed.indentLength;
      const existingLevel = rawIndentStack.lastIndexOf(rawIndent);
      let level: number;
      if (rawIndentStack.length === 0) {
        rawIndentStack = [rawIndent];
        normalizedStepStack = [];
        level = 0;
      } else if (existingLevel >= 0) {
        rawIndentStack = rawIndentStack.slice(0, existingLevel + 1);
        normalizedStepStack = normalizedStepStack.slice(0, existingLevel + 1);
        level = existingLevel;
      } else {
        const parentIndent = rawIndentStack[rawIndentStack.length - 1];
        const increase = rawIndent - parentIndent;
        if (increase >= 1 && increase <= 4) {
          rawIndentStack.push(rawIndent);
          level = rawIndentStack.length - 1;
        } else {
          rawIndentStack = [rawIndent];
          normalizedStepStack = [];
          level = 0;
        }
      }

      const normalizedIndent = normalizedStepStack.slice(0, level).reduce((total, width) => total + width, 0);
      normalizedStepStack[level] = Math.max(4, parsed.markerWidth);
      appendOriginal(lineStart, parsed.indentStart);
      appendNormalizedSpaces(normalizedIndent, parsed.indentStart, parsed.indentLength);
      appendOriginal(parsed.indentStart + parsed.indentLength, lineEnd);
    } else {
      appendOriginal(lineStart, lineEnd);
      if (line.trim() && !markerProtected && !isIndentedContinuation(line, rawIndentStack[0], activeContainer)) {
        activeContainer = "";
        rawIndentStack = [];
        normalizedStepStack = [];
      }
    }

    if (breakOffset === -1) break;
    appendOriginal(breakOffset, breakOffset + 1);
    lineStart = breakOffset + 1;
  }

  outputToInputOffset.push(source.length);
  return { source: output, outputToInputOffset };
}

function parseListLine(line: string, absoluteLineStart: number): ListLine | null {
  const quoteMatch = /^((?: {0,3}> ?)+)( *)((?:[-+*]|\d{1,9}[.)])[ \t]+)/.exec(line);
  if (quoteMatch) {
    const indentStart = quoteMatch[1].length;
    return {
      containerKey: `quote:${(quoteMatch[1].match(/>/g) ?? []).length}`,
      indentStart: absoluteLineStart + indentStart,
      indentLength: quoteMatch[2].length,
      markerOffset: absoluteLineStart + indentStart + quoteMatch[2].length,
      markerWidth: quoteMatch[3].length
    };
  }

  const rootMatch = /^( *)((?:[-+*]|\d{1,9}[.)])[ \t]+)/.exec(line);
  if (!rootMatch) return null;
  return {
    containerKey: "root",
    indentStart: absoluteLineStart,
    indentLength: rootMatch[1].length,
    markerOffset: absoluteLineStart + rootMatch[1].length,
    markerWidth: rootMatch[2].length
  };
}

function isIndentedContinuation(line: string, rootIndent: number | undefined, activeContainer: string): boolean {
  if (rootIndent === undefined) return false;
  if (activeContainer === "root") return (/^ */.exec(line)?.[0].length ?? 0) > rootIndent;
  if (!activeContainer.startsWith("quote:")) return false;
  const quoteMatch = /^((?: {0,3}> ?)+)( *)/.exec(line);
  return Boolean(quoteMatch && `quote:${(quoteMatch[1].match(/>/g) ?? []).length}` === activeContainer && quoteMatch[2].length > rootIndent);
}
